import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Meta } from 'src/components/Meta';
import { StickyCard } from 'src/components/StickyCard';
import { useSession } from 'src/contexts/SessionContext';
import { GroupAssessment } from 'src/models/assessment/_types';
import {
  fetchGroupAssessment,
  newGroupAssessment,
  updateGroupAssessment,
} from 'src/models/assessment/assessment';
import { fetchGroup } from 'src/models/group/group';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { FormAssessmentSelect } from 'src/shared/Forms/Selects/AssessmentSelect';
import { FormTagsSelect } from 'src/shared/Forms/Selects/TagsSelect';
import { Scheduling } from 'src/shared/Scheduling';
import { Identifier } from 'src/types/utilityTypes';
import toast from 'src/utils/toast';

export const NewGroupAssessmentPage = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const { t } = useTranslation('translation', {
    keyPrefix: 'group.assessment',
  });
  const { t: tButtons } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });
  const { session } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: group } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => fetchGroup({ id: groupId as Identifier }),
  });

  const { data: groupAssessment } = useQuery({
    queryKey: ['groupAssessment', assessmentId],
    queryFn: () =>
      fetchGroupAssessment({ groupAssessmentId: assessmentId as Identifier }),
    enabled: !!assessmentId && assessmentId !== 'new',
  });

  const createGroupAssessmentMutation = useMutation({
    mutationFn: (data: Partial<GroupAssessment>) =>
      newGroupAssessment({ data }),
    onSuccess: () => {
      toast.success(t('Group assessment created successfully'));
      queryClient.invalidateQueries(['groupAssessments']);
      navigate(`/groups/${groupId}/assessments`, { replace: true });
    },
    onError: () => {
      toast.error(t('Failed to create group assessment'));
    },
  });

  const updateGroupAssessmentMutation = useMutation({
    mutationFn: (data: Partial<GroupAssessment>) =>
      updateGroupAssessment({
        data,
        groupAssessmentId: assessmentId as Identifier,
      }),
    onSuccess: () => {
      toast.success(t('Group assessment updated successfully'));
      queryClient.invalidateQueries(['groupAssessments']);
      navigate(`/groups/${groupId}/assessments`, { replace: true });
    },
    onError: () => {
      toast.error(t('Failed to update group assessment'));
    },
  });

  const formikProps = {
    initialValues: {
      groupId: groupId,
      assessmentId: groupAssessment?.assessmentId || '',
      tags: groupAssessment?.tags || [],
      delayInterval: groupAssessment?.delayInterval || '',
      expiresAt: groupAssessment?.expiresAt || '',
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      if (assessmentId === 'new') {
        createGroupAssessmentMutation.mutateAsync(values);
      } else {
        updateGroupAssessmentMutation.mutateAsync(values);
      }
    },
  };

  return (
    <Formik {...formikProps}>
      {({ submitForm }) => (
        <>
          <AppFrameConfig
            id="groupAssessment"
            containerMaxWidth={'xl'}
            backButtonTitle={'assessments'}
            backButtonLinkTo={`/groups/${groupId}/assessments`}
            title={
              assessmentId === 'new'
                ? t('New Group Assessment')
                : t('Editing Group Assessment')
            }
          />
          <StickyCard
            title={group?.name}
            actions={
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  loading={
                    createGroupAssessmentMutation.isLoading ||
                    updateGroupAssessmentMutation.isLoading
                  }
                  onClick={() => {
                    submitForm();
                  }}
                >
                  {tButtons('save')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    navigate(`/groups/${groupId}/assessments`, {
                      replace: true,
                    });
                  }}
                >
                  {tButtons('cancel')}
                </Button>
              </Box>
            }
          >
            <Meta
              title={
                assessmentId === 'new'
                  ? t('New Group Assessment')
                  : t('Editing Group Assessment')
              }
              brand={session?.brand}
            />
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}
            >
              <FormAssessmentSelect
                name="assessmentId"
                label={t('one')}
                placeholder={t('Select Assessment')}
                disabled={createGroupAssessmentMutation.isLoading}
              />
              <FormTagsSelect
                name="tags"
                label={t('Tags')}
                tagType="groupAssessment"
                multiple
              />
              <Scheduling type={t('one').toLowerCase()} />
            </Box>
          </StickyCard>
        </>
      )}
    </Formik>
  );
};
